import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "kicker", "badgeStage", "title", "continueButton"]
  static values = {
    presentUrl: String
  }

  connect() {
    this.unpresentedCards = Array.from(this.element.querySelectorAll('[data-unpresented]'))
    this.currentCard = null
    this.revealed = false
    this.flickerTimeout = null

    if (this.unpresentedCards.length === 0) return

    this.startPresentation(this.unpresentedCards.shift())
  }

  disconnect() {
    this.clearFlickerTimeout()
  }

  startPresentation(card) {
    this.currentCard = card
    this.revealed = false
    this.badgeStageTarget.innerHTML = ''
    this.titleTarget.textContent = ''
    this.titleTarget.hidden = true
    this.continueButtonTarget.hidden = true
    this.kickerTarget.textContent = 'You got a new trophy...'

    const badgeWrap = card.querySelector('.trophy-badge-wrap')
    if (badgeWrap) {
      const clone = badgeWrap.cloneNode(true)
      clone.classList.add('trophy-presentation__badge')
      clone.style.setProperty('--trophy-fill', card.style.getPropertyValue('--trophy-fill'))
      clone.style.setProperty('--trophy-border', card.style.getPropertyValue('--trophy-border'))
      this.badgeStageTarget.appendChild(clone)
    }

    this.overlayTarget.classList.remove('is-revealed')
    this.overlayTarget.classList.add('is-open', 'is-flickering')
    this.overlayTarget.setAttribute('aria-hidden', 'false')
    this.overlayTarget.focus()
    this.loadConfettiLibrary().catch(() => {})

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.reveal()
      return
    }

    // Pokemon-style evolution: keep the silhouette flickering for a few seconds, then reveal.
    this.flickerTimeout = window.setTimeout(() => {
      this.reveal()
    }, 3200)
  }

  reveal() {
    this.clearFlickerTimeout()
    if (this.revealed || !this.currentCard) return

    this.revealed = true
    this.overlayTarget.classList.remove('is-flickering')
    this.overlayTarget.classList.add('is-revealed')
    this.titleTarget.textContent = this.currentCard.dataset.trophyTitle || ''
    this.titleTarget.hidden = false
    this.continueButtonTarget.hidden = false
    this.continueButtonTarget.focus()
    this.throwConfetti().catch(() => {})
  }

  dismiss(event) {
    if (event) event.preventDefault()
    if (!this.revealed || !this.currentCard) return

    const trophyId = this.currentCard.dataset.trophyId
    this.overlayTarget.classList.remove('is-open', 'is-flickering', 'is-revealed')
    this.overlayTarget.setAttribute('aria-hidden', 'true')
    this.badgeStageTarget.innerHTML = ''
    this.currentCard = null
    this.revealed = false

    this.markPresented(trophyId)

    if (this.unpresentedCards.length > 0) {
      window.setTimeout(() => {
        this.startPresentation(this.unpresentedCards.shift())
      }, 400)
    }
  }

  dismissOnBackdrop(event) {
    if (event.target !== this.overlayTarget) return
    this.dismiss(event)
  }

  dismissOnEscape(event) {
    if (event.key !== 'Escape') return
    this.dismiss(event)
  }

  clearFlickerTimeout() {
    if (this.flickerTimeout) {
      window.clearTimeout(this.flickerTimeout)
      this.flickerTimeout = null
    }
  }

  async markPresented(trophyId) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    if (!this.presentUrlValue || !token || !trophyId) return

    const body = new URLSearchParams()
    body.append('trophy_id', trophyId)

    try {
      await fetch(this.presentUrlValue, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: body
      })
    } catch (e) {
      // If this fails, the animation will play again on the next visit.
    }
  }

  async throwConfetti() {
    if (!window.confetti) {
      await this.loadConfettiLibrary()
    }
    if (!window.confetti) return

    window.confetti({
      particleCount: 140,
      spread: 90,
      origin: { y: 0.42 },
      zIndex: 12000,
      colors: ['#FFD81B', '#26A428', '#ffffff', '#C9A00E', '#4da3ff']
    })
    window.setTimeout(() => {
      window.confetti({
        particleCount: 80,
        spread: 110,
        origin: { y: 0.5 },
        zIndex: 12000,
        colors: ['#FFD81B', '#26A428', '#ffffff', '#C9A00E']
      })
    }, 280)
  }

  loadConfettiLibrary() {
    if (window.confetti) return Promise.resolve()
    if (this.confettiLoading) return this.confettiLoading

    this.confettiLoading = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js'
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })

    return this.confettiLoading
  }
}
